import classes from "./NewPackagingSelectionItem.module.less";
import type { PackagingInformationEntry } from "~/models/ProductData";
import { Card } from "~/Configurators/Linear/components/Card";
import { type ReactNode, useMemo } from "react";
import { Box, Divider, FormControlLabel, Radio, Stack, Typography } from "@mui/material";
import { getStockLevel, StockIndicatorIcon } from "~/common/components/StockIndicator/StockIndicator";
import { CountryOfOriginDescriptorParam } from "~/common/components/Product/ProductParams";
import { useTranslation } from "react-i18next";
import { getPackagingLabel } from "~/Cart/cartUtils";
import dayjs from "dayjs";
import { useIsPackagePhasedOut } from "~/ProductDetails/components/PhaseInOut/usePhaseInOutLogic";
import { DiscontinuationNotification } from "~/ProductDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { type PackagingSelectionVariant } from "~/common/components/PackagingSelection/PackagingSelectionVariant";
import { Stacker } from "~/common/components/Stacker/Stacker";
import classNames from "classnames";
import { NumberFormatter } from "~/utils/NumberFormatter";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { C_OUTLINE, C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";

interface Props {
  className?: string;
  pack: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
  selected?: boolean;
  onSelect?: () => void;
  variant: PackagingSelectionVariant;
  disabled?: boolean;
}

export const NewPackagingSelectionItem = ({ selected, onSelect, productDescriptor, pack, disabled }: Props) => {
  const { t } = useTranslation();

  const packagingLabel = getPackagingLabel({
    t,
    productDescriptor,
    packSize: pack.packSize,
    packCode: pack.packCode,
  });

  const showDiscontinuedWarning = useIsPackagePhasedOut(productDescriptor, pack.packCode);
  const stockCalculations = useInStockCalculations(pack);

  const discontinuationNotification = showDiscontinuedWarning && (
    <DiscontinuationNotification
      className={classes.discontinuationNotification}
      discontinued={productDescriptor}
      variant={"package"}
    />
  );

  const isPackageOnSale = useSalesHelper(productDescriptor).isPackageOnSale(pack.packCode);

  const header = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <FormControlLabel
        key={pack.packCode}
        value={pack.packCode}
        checked={selected}
        control={<Radio sx={{ my: "-8px" }} color="onSecondaryContainer" />}
        label={<Typography variant="titleMedium">{packagingLabel}</Typography>}
        onChange={onSelect}
        disabled={disabled}
      />
      {isPackageOnSale && <SaleTag size={16} />}
    </Box>
  );

  const stockContent = (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <StockIndicatorValue totalInStock={stockCalculations.totalInStock} />
      <InProductionSection pack={pack} />
      <Stack direction={"row"} gap={0.5} alignItems={"center"}>
        <PublicOutlinedIcon fontSize="small" />
        <CountryOfOriginDescriptorParam
          showEmpty
          descriptor={productDescriptor}
          packCode={pack.packCode}
          withoutLabel={true}
        />
      </Stack>
    </Box>
  );

  const handleClickOnPackage = () => {
    if (onSelect) {
      onSelect();
    }
  };

  return (
    <Card className={classes.root} active={selected} onClick={handleClickOnPackage}>
      <CardContent className={classNames(classes.packageCard, selected && classes.packSelected)}>
        {discontinuationNotification}
        <Stack divider={<Divider sx={{ color: selected ? C_OUTLINE : C_OUTLINE_VARIANT }} />} gap={1}>
          {header}
          {stockContent}
        </Stack>
      </CardContent>
    </Card>
  );
};

const getCompactNumber = (value: number) => {
  const absValue = Math.abs(value);

  // If a number has more than 5 digits, round it up to the nearest thousand
  if (absValue < 10000) {
    return NumberFormatter.getDefaultFormatter().format(value);
  } else {
    const rounded = Math.round(absValue / 1000);
    return absValue > rounded * 1000 ? `${rounded}k+` : `${rounded}k`;
  }
};

const useInStockCalculations = (pack: PackagingInformationEntry) => {
  const { t } = useTranslation();
  return useMemo(() => {
    const readyForShipment = pack.totalWarehouse.inStock.find((entry) => !entry.date)?.quantity ?? 0;
    const inStockEntries = pack.totalWarehouse.inStock.map(
      (entry) =>
        [
          entry.quantity,
          entry.date
            ? dayjs(entry.date).format("DD.MM.YYYY")
            : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" }),
        ] as const,
    );
    const totalInStock = inStockEntries.reduce((acc, [quantity]) => acc + quantity, 0);
    return { readyForShipment, inStockEntries, totalInStock };
  }, [pack]);
};

const StockIndicatorValue = ({ totalInStock, className }: { totalInStock: number; className?: string }) => {
  const { t } = useTranslation();

  return (
    <Stacker direction={"row"} gap={8} className={classNames("d-flex flex-align-center", className)}>
      <StockIndicatorIcon className={classes.stockIcon} level={getStockLevel(totalInStock)} />
      <p>
        {totalInStock > 0 && t("stockIndicator.existsWithQty", { amount: getCompactNumber(totalInStock) })}
        {totalInStock === 0 && t("addToCartPopup.stock.info")}
      </p>
    </Stacker>
  );
};

const InProductionSection = ({ pack }: { pack: PackagingInformationEntry }) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();

  const inProductionEntries = pack.totalWarehouse.inProduction.map(
    (entry) => [entry.quantity, dayjs(entry.date).format("DD.MM.YYYY")] as const,
  );

  if (pack.totalWarehouse.inClarification > 0) {
    inProductionEntries.unshift([
      pack.totalWarehouse.inClarification,
      t("product.availability.stock.table.inClarification"),
    ] as const);
  }
  const totalInProduction = inProductionEntries.reduce((acc, [quantity]) => acc + quantity, 0);
  return (
    <>
      {totalInProduction > 0 && (
        <Stacker direction={"row"} gap={8} className={classes.perPack}>
          <CalendarTodayOutlinedIcon fontSize="small" />
          {!isTablet ? (
            <Typography>{getCompactNumber(totalInProduction)}</Typography>
          ) : (
            <Typography>
              {t("productDetailsPage.availabilityTable.toCome", {
                quantity: getCompactNumber(totalInProduction),
              })}
            </Typography>
          )}
        </Stacker>
      )}
    </>
  );
};

const CardContent = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={classNames(className, classes.cardContent)}>{children}</div>;
};
